// w-faq-section.js — version "anti-jump" simple : close instant, open animated + per-frame scroll lock

class FaqSectionV2 extends HTMLElement {
    constructor() {
        super();
        this._busy = false;
        this._duration = Number(this.getAttribute('data-duration')) || 250;
        this._easing = 'cubic-bezier(.2,.8,.2,1)';
    }

    connectedCallback() {
        // init states
        this.querySelectorAll('.faq-item-container').forEach(item => {
            const txt = item.querySelector('.faq-item-text');
            if (!item.classList.contains('faq-item-active')) {
                txt.style.display = 'none';
                txt.style.height = '';
            } else {
                // s'assure que l'item ouvert est propre
                txt.style.display = 'block';
                txt.style.height = '';
            }
            txt.style.overflow = 'hidden';
            txt.style.willChange = 'height';
        });

        this.querySelectorAll('.faq-item-title').forEach(btn => {
            btn.setAttribute('role', 'button');
            btn.setAttribute('tabindex', '0');
            btn.addEventListener('click', (ev) => this.onClick(ev));
            btn.addEventListener('keydown', (ev) => {
                if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); this.onClick(ev); }
            });
        });
    }

    // --- utils ---
    itemFromBtn(btn) { return btn.closest('.faq-item-container'); }
    textFromItem(item) { return item.querySelector('.faq-item-text'); }
    titleFromItem(item) { return item.querySelector('.faq-item-title'); }
    setIcon(btn, open) {
        const wrap = btn.querySelector('.faq-close');
        if (wrap) wrap.classList.toggle('faq-display-none', open);
    }

    // ferme un item SANS animation (instantané → pas de saut)
    closeItemInstant(item) {
        if (!item.classList.contains('faq-item-active')) return;
        const txt = this.textFromItem(item);
        item.classList.remove('faq-item-active');
        this.setIcon(this.titleFromItem(item), false);
        txt.style.transition = 'none';
        txt.style.height = '';
        txt.style.display = 'none';
        // force reflow puis retire transition: none
        // eslint-disable-next-line no-unused-expressions
        txt.offsetHeight;
        txt.style.transition = '';
    }

    // anime l'ouverture d'un item (unique animation)
    async openItemAnimated(item, duration = this._duration) {
        const txt = this.textFromItem(item);

        // préparer le bloc
        txt.style.display = 'block';
        txt.style.height = '0px';
        // mesurer la cible
        const target = txt.scrollHeight;

        // état + icône
        item.classList.add('faq-item-active');
        this.setIcon(this.titleFromItem(item), true);

        // transition CSS
        txt.style.transition = `height ${duration}ms ${this._easing}`;
        // reflow
        // eslint-disable-next-line no-unused-expressions
        txt.offsetHeight;

        const done = () => {
            txt.removeEventListener('transitionend', onEnd);
            txt.style.transition = '';
            txt.style.height = '';
        };
        const onEnd = (evt) => {
            if (evt.propertyName === 'height') done();
        };

        // Lancer l'anim
        txt.addEventListener('transitionend', onEnd, { once: true });
        txt.style.height = `${target}px`;

        // filet de secours si transitionend ne se déclenche pas
        await new Promise(res => setTimeout(() => { done(); res(); }, duration + 60));
    }

    // maintient le bouton cliqué fixe à l'écran pendant une fonction async
    async keepButtonFixedDuring(btn, asyncWork) {
        const m = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (m.matches) return asyncWork(); // pas d'animation → pas de compensation

        let running = true;
        const startTop = btn.getBoundingClientRect().top;

        const loop = () => {
            if (!running) return;
            const nowTop = btn.getBoundingClientRect().top;
            const delta = nowTop - startTop;
            if (delta) window.scrollBy(0, delta);
            requestAnimationFrame(loop);
        };

        requestAnimationFrame(loop);
        try {
            await asyncWork();
        } finally {
            running = false;
        }
    }

    async onClick(e) {
        if (this._busy) return;
        this._busy = true;

        const btn = e.currentTarget;
        const item = this.itemFromBtn(btn);
        const isOpen = item.classList.contains('faq-item-active');

        // séquence : fermer tout instantanément, puis ouvrir (si différent)
        await this.keepButtonFixedDuring(btn, async () => {
            // 1) fermer les autres sans animation
            this.querySelectorAll('.faq-item-container.faq-item-active').forEach(it => {
                if (it !== item || isOpen) this.closeItemInstant(it);
            });

            // 2) si l'item cliqué était ouvert → on s'arrête (accordéon = 0 ouvert possible)
            if (isOpen) return;

            // 3) ouvrir l'item cliqué avec UNE seule anim
            await this.openItemAnimated(item);
        });

        this._busy = false;
    }
}

customElements.define('faq-section-v2', FaqSectionV2);

// petit confort : au chargement, empêcher l'ancrage auto
(() => {
    const css = document.createElement('style');
    css.textContent = `
    .section-faq-section, .faq-item-container, .faq-item-text { overflow-anchor: none; }
    .faq-item-text { will-change: height; }
  `;
    document.head.appendChild(css);
})();
